// Reusable "full screen" toggle for dashboard-style views.
//
// Usage in a component:
//   import fullscreenToggle from '../mixins/fullscreenToggle'
//   export default { mixins: [fullscreenToggle], ... }
//
// Then in the template, bind the root element's class and add a button:
//   <div :class="{ 'is-fullscreen': isFullscreen }">
//     <a-button @click="toggleFullscreen">
//       <i :class="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'" aria-hidden="true"></i>
//       {{ isFullscreen ? 'Exit full screen' : 'Full screen' }}
//     </a-button>
//   </div>
//
// Pair the `is-fullscreen` class with CSS such as:
//   .my-view.is-fullscreen {
//     position: fixed; top: 0; right: 0; bottom: 0; left: 0;
//     z-index: 1000; overflow-y: auto; background: #f8f9fe;
//   }
// (z-index 1000 stays below Ant Design's popup layer (1050) so dropdowns
// and date pickers, which render on <body>, still show on top.)
export default {
  data() {
    return {
      isFullscreen: false
    };
  },
  mounted() {
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    // Don't leave the browser stuck in fullscreen after navigating away
    if (this.isFullscreen) {
      this.exitFullscreen();
    }
  },
  methods: {
    toggleFullscreen() {
      if (this.isFullscreen) {
        this.exitFullscreen();
      } else {
        this.enterFullscreen();
      }
    },

    // Maximize the view over the app chrome (CSS, via the `isFullscreen` flag),
    // and also ask the browser for real fullscreen on the whole page. The page
    // element (not just this component) is used because Ant Design dropdowns /
    // date pickers render on <body> and would be hidden inside a fullscreen
    // sub-element. If the browser refuses fullscreen, the CSS-maximized view
    // still works on its own.
    enterFullscreen() {
      this.isFullscreen = true;
      const el = document.documentElement;
      const request = el.requestFullscreen || el.webkitRequestFullscreen;
      if (request) {
        try {
          const result = request.call(el);
          if (result && result.catch) {
            result.catch(() => {});
          }
        } catch (e) {
          // ignore: CSS-maximized view stays active
        }
      }
    },

    exitFullscreen() {
      this.isFullscreen = false;
      const active = document.fullscreenElement || document.webkitFullscreenElement;
      if (active) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) {
          try {
            const result = exit.call(document);
            if (result && result.catch) {
              result.catch(() => {});
            }
          } catch (e) {
            // ignore
          }
        }
      }
    },

    // Browser left fullscreen (e.g. user pressed Esc): drop the maximized view too
    onFullscreenChange() {
      const active = document.fullscreenElement || document.webkitFullscreenElement;
      if (!active && this.isFullscreen) {
        this.isFullscreen = false;
      }
    }
  }
};
